import { Fragment, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, Download } from '../../../icons';
import { getEmployee, getEmployeeCard, EMPLOYMENT_STATUS_LABEL, type EmployeeCardDto } from '../../../api/hr-employee';
import { employmentTypeOptions } from '../../../lib/employment-type-i18n';
import { getSharedLang, subscribeSharedLang } from '../../../lib/base-i18n';
import { useCompanyCodes } from '../../../lib/company-codes';
import { useBrand } from '../../../lib/brand-logo';
import { avatarImageUrl } from '../../../lib/avatar-src';
import { NOT_READY_TEXT } from './employee-hr-types';
import { EMPLOYEES_PATH, STATUS_TONE, UPCOMING_HIRE_LABEL, employeePath, initialOf, isUpcomingHire } from './employees-shared';
import './employees-hr.css';
import './employee-card.css';

/**
 * 인사카드 — 인쇄용 (2026-09-13 · 과제 J · `/erp/hr/employees/:userId/card`).
 *
 * 시안 `axi-employee-hr-card`(사용자 확인 — 그대로 반영 · A4 2쪽 허용 · 직인 · 결재란 없음).
 * ★ 서버 `GET /hr/employees/:id/card`(계약 §18.7) JSON 을 **그대로** 그린다 — 화면이 값을 새로 계산하지 않는다.
 * ★ 화면 = 미리보기 + 「인쇄 · PDF 저장」, 인쇄는 브라우저에 맡긴다(PDF 라이브러리 없음 · `@media print` 가 종이만 남긴다 —
 *   증명서 `CertificateDoc` 과 같은 방식).
 * ★ 카드를 부를 때마다 서버 감사(`hr.employee.card_exported`)에 남는다. 회사 이메일 · 연차 · 근무제는 싣지 않는다.
 * ★ 가족은 서버가 `null`(표가 아직 없다) — 자리만 그린다.
 */
const CARD_ERRORS: Record<number, string> = {
  403: '이 사람의 인사카드를 볼 권한이 없습니다.',
  404: '없는 직원입니다.',
  429: '요청이 많습니다 — 잠시 후 다시 시도해 주세요.',
};
const PROOF_LABEL: Record<string, string> = { ALL: '증빙 전부', SOME: '증빙 일부', NONE: '증빙 없음' };
const EXPIRY_BADGE: Record<string, [string, 'ok' | 'warn' | 'bad'] | undefined> = {
  VALID: ['유효', 'ok'], SOON: ['만료 임박', 'warn'], EXPIRED: ['만료', 'bad'],
};

/** 인쇄물에 적는 시각 — 보는 사람의 벽시계로 `YYYY-MM-DD HH:mm`. */
function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Kv({ rows }: { rows: Array<Array<[string, ReactNode]>> }) {
  return (
    <table className="hrc-kv">
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>{row.map(([k, v]) => <Fragment key={k}><th scope="row">{k}</th><td>{v}</td></Fragment>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

function RecordTable({ cols, rows, empty }: { cols: string[]; rows: ReactNode[][]; empty: string }) {
  return (
    <table className="hrc-tbl">
      <thead><tr>{cols.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0
          ? <tr className="none"><td colSpan={cols.length}>{empty}</td></tr>
          : rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
      </tbody>
    </table>
  );
}

const side = (s: { teamName: string | null; position: string | null }) => [s.teamName, s.position].filter(Boolean).join(' · ') || '—';

export function EmployeeCardPage(): JSX.Element {
  const { userId = '' } = useParams();
  const { companyName } = useBrand();
  const codes = useCompanyCodes('hr');
  const lang = useSyncExternalStore(subscribeSharedLang, getSharedLang, getSharedLang);
  const types = codes.apply('employment_type', employmentTypeOptions(lang));

  const [card, setCard] = useState<EmployeeCardDto | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null); setCard(null); setPhoto(null);
    getEmployeeCard(userId)
      .then((c) => { if (alive) setCard(c); })
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status ?? 0;
        if (alive) setError(CARD_ERRORS[status] ?? '인사카드를 불러오지 못했습니다.');
      })
      .finally(() => { if (alive) setLoading(false); });
    // 사진 주소는 카드에 없다 — 상세에서 빌린다. 못 읽으면 이름 첫 글자.
    getEmployee(userId)
      .then((d) => { if (alive) setPhoto(avatarImageUrl(userId, d.self.avatarUrl)); })
      .catch(() => { /* 사진 칸만 비운다 */ });
    return () => { alive = false; };
  }, [userId]);

  const p = card?.person;
  const upcoming = p ? isUpcomingHire(p) : false;
  const generated = card ? stamp(card.generatedAt) : '';

  return (
    <div className="hre">
      <header className="hre-top hrc-noprint">
        <div>
          <nav className="hre-crumb" aria-label="위치">
            <span>인사</span><i>/</i><Link to={EMPLOYEES_PATH}>직원 관리</Link><i>/</i>
            <Link to={employeePath(userId)}>{p?.name ?? '직원'}</Link><i>/</i><span>인사카드</span>
          </nav>
          <h1 className="hre-title">인사카드</h1>
        </div>
      </header>

      {error && <p className="hre-alert hrc-noprint" role="alert"><AlertCircle aria-hidden />{error}</p>}

      <div className="hrc-bar hrc-noprint">
        <span><b>인사카드 미리보기</b> — 인쇄하면 이 종이만 나갑니다(A4)</span>
        <Link className="hre-btn hre-btn--line hre-btn--sm" to={employeePath(userId)}>상세로</Link>
        <button type="button" className="hre-btn hre-btn--sm" disabled={!card} onClick={() => window.print()}>
          <Download aria-hidden />인쇄 · PDF 저장
        </button>
      </div>

      {loading && !card && <p className="hre-path">불러오는 중…</p>}

      {card && p && (
        <article className="hrc-sheet" aria-label="인사카드">
          <header className="hrc-head">
            <div className="hrc-brand"><b>{companyName ?? '회사'}</b><span>인사관리</span></div>
            <h2 className="hrc-title">인사카드</h2>
            <dl className="hrc-issued">
              <div><dt>출력</dt><dd className="num">{generated}</dd></div>
              <div><dt>사번</dt><dd className="num">{p.employeeNumber ?? '—'}</dd></div>
            </dl>
          </header>

          <section className="hrc-id" aria-label="인적 사항">
            <span className="hrc-photo">
              {photo ? <img src={photo} alt={`${p.name} 사진`} /> : <span aria-hidden>{initialOf(p.name)}</span>}
            </span>
            <Kv rows={[
              [['성명', <b key="n">{p.name}</b>], ['영문 성명', p.nameEn ?? '—']],
              [['생일', <span key="b" className="num">{p.birthday ?? '—'}</span>], ['휴대폰', <span key="t" className="num">{p.phone ?? '—'}</span>]],
              [['입사일', <span key="h" className="num">{p.hireDate ?? '—'}</span>], ['근속', p.tenureLabel ?? '—']],
              [
                ['고용 형태', types.find((t) => t.key === p.employmentType)?.label ?? p.employmentType ?? '미설정'],
                ['재직 상태', upcoming
                  ? <span key="s" className="hre-badge hre-badge--pur">{UPCOMING_HIRE_LABEL}</span>
                  : <span key="s" className={`hre-badge hre-badge--${STATUS_TONE[p.employmentStatus] ?? 'slate'}`}>{EMPLOYMENT_STATUS_LABEL[p.employmentStatus] ?? p.employmentStatus}</span>],
              ],
            ]} />
          </section>

          <section className="hrc-sec" aria-label="소속 · 직위">
            <h3>소속 · 직위</h3>
            <Kv rows={[
              [['부서', p.divisionName ?? '—'], ['팀', p.teamName ?? '팀 미배정']],
              [['근무지', p.worksiteName ?? '본사 소속'], ['직위', p.position ?? '—']],
              [
                ['호칭', p.title ?? '—'],
                ['직책', p.jobRoles.length === 0 ? '없음' : (
                  <Fragment key="r">
                    {p.jobRoles.map((j) => (
                      <span key={`${j.name}-${j.startDate}`} className="hrc-line">
                        {j.name}{j.orgName ? ` · ${j.orgName}` : ''}{j.isConcurrent ? ' (겸직)' : ''} <span className="hrc-sub num">{j.startDate}~</span>
                      </span>
                    ))}
                  </Fragment>
                )],
              ],
            ]} />
          </section>

          <section className="hrc-sec" aria-label="경력">
            <h3>경력 <small>합계 {card.recordsSummary.careerYears}년 · {card.careers.length}건 · {PROOF_LABEL[card.recordsSummary.proof] ?? ''}</small></h3>
            <RecordTable
              cols={['회사', '직위 · 직무', '기간', '인정', '증빙']}
              empty="경력이 없습니다"
              rows={card.careers.map((c) => [
                <b key="c">{c.companyName}</b>,
                [c.position, c.duty].filter(Boolean).join(' · ') || '—',
                <span key="p" className="num">{c.startYm} ~ {c.endYm}</span>,
                <span key="y" className="num">{c.years}년</span>,
                c.confirmedAt
                  ? <span key="f" className="hre-badge hre-badge--ok">확인</span>
                  : <span key="f" className="hre-badge hre-badge--slate">{c.hasProof ? '증빙 있음' : '증빙 없음'}</span>,
              ])}
            />
          </section>

          <section className="hrc-sec" aria-label="학력">
            <h3>학력 {card.recordsSummary.finalLevelLabel && <small>최종 {card.recordsSummary.finalLevelLabel}{card.recordsSummary.major ? ` · ${card.recordsSummary.major}` : ''}</small>}</h3>
            <RecordTable
              cols={['학교', '전공', '학위', '재학 기간', '상태']}
              empty="학력이 없습니다"
              rows={card.educations.map((e) => [
                <b key="s">{e.schoolName}</b>,
                e.major ?? '—',
                <span key="l" className="hre-badge hre-badge--slate">{e.levelLabel}</span>,
                <span key="p" className="num">{e.startYm ?? '—'} ~ {e.endYm ?? '—'}</span>,
                <span key="t" className={`hre-badge hre-badge--${e.state === 'GRADUATED' ? 'ok' : 'slate'}`}>{e.stateLabel}</span>,
              ])}
            />
          </section>

          <section className="hrc-sec" aria-label="자격 · 어학">
            <h3>자격 · 어학</h3>
            <RecordTable
              cols={['구분', '명칭', '발급 기관 · 점수', '취득 · 응시일', '유효 기한']}
              empty="자격 · 어학이 없습니다"
              rows={card.credentials.map((c) => {
                const badge = EXPIRY_BADGE[c.expiry];
                return [
                  <span key="k" className={`hre-badge hre-badge--${c.kind === 'LANGUAGE' ? 'pur' : 'slate'}`}>{c.kindLabel}</span>,
                  <b key="n">{c.name}</b>,
                  (c.kind === 'LANGUAGE' ? c.score ?? c.issuer : c.issuer ?? c.score) ?? '—',
                  <span key="a" className="num">{c.acquiredOn}</span>,
                  <span key="e"><span className="num">{c.expiresOn ?? '없음'}</span>{badge && <> <span className={`hre-badge hre-badge--${badge[1]}`}>{badge[0]}</span></>}</span>,
                ];
              })}
            />
          </section>

          <section className="hrc-sec" aria-label="발령 이력">
            <h3>발령 이력 <small>시행된 발령만</small></h3>
            <RecordTable
              cols={['시행일', '구분', '발령 전', '발령 후', '문서 번호']}
              empty="시행된 발령이 없습니다"
              rows={card.assignments.map((a) => [
                <span key="d" className="num">{a.effectiveDate}</span>,
                <span key="k" className={`hre-badge hre-badge--${a.roleKind ? 'pur' : 'slate'}`}>{a.kindLabel}</span>,
                side(a.before),
                side(a.after),
                <span key="n" className="num">{a.docNo ?? '—'}</span>,
              ])}
            />
          </section>

          <section className="hrc-sec" aria-label="가족">
            <h3>가족 <small>부양가족</small></h3>
            <RecordTable
              cols={['관계', '성명', '생년월일', '부양가족', '비고']}
              empty={`${NOT_READY_TEXT} — 가족 표는 마이그레이션 판 뒤에 채워집니다`}
              rows={[]}
            />
          </section>

          <footer className="hrc-foot">
            <span>출력 시각({generated})의 인사 정보입니다 · 회사 이메일 · 연차 · 근무제는 싣지 않습니다</span>
            <span>출력 기록은 감사 로그에 남습니다</span>
          </footer>
        </article>
      )}
    </div>
  );
}

export default EmployeeCardPage;
